import React, { useState, useContext } from 'react';
import { sendOtp, verifyOtp, saveName } from '../services/authService';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './auth.css';
import cardIllustration from '../assets/card-illustration.png';

const CheckCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="#34C759" strokeWidth="1.5" />
    <path
      d="M7.5 12L10.5 15L16.5 9"
      stroke="#34C759"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SpinnerIcon = () => <span className="auth-btn__spinner" />;

const FEATURES = [
  'Credit Card Reviews & Comparisons',
  'Educational Content & Guides',
  'Personalized Consulting Services',
];

// ✅ LeftPanel now returns a single wrapper div with auth-left INSIDE auth-page__left-panel
const LeftPanel = () => (
  <div className="auth-page__left-panel">
    <div className="auth-left">
      <h2 className="auth-left__title">Why Card Academy?</h2>
      <div className="auth-left__illustration">
        <img src={cardIllustration} alt="Credit Cards" />
      </div>
      <div className="auth-left__features">
        {FEATURES.map((f) => (
          <div key={f} className="auth-left__feature">
            <div className="auth-left__feature-icon">
              <CheckCircleIcon />
            </div>
            <span className="auth-left__feature-text">{f}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function LoginPage({ preventRedirect = false, modal = false }) {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [verifiedToken, setVerifiedToken] = useState(null);
  const [verifiedUser, setVerifiedUser] = useState(null);

  const timerRef = React.useRef(null);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const formatMobile = () => {
    let cleaned = mobile.trim().replace(/\D/g, '');
    if (cleaned.length === 10) cleaned = `91${cleaned}`;
    if (cleaned.length !== 12 || !cleaned.startsWith('91')) return null;
    return cleaned;
  };

  const startTimer = (seconds = 60) => {
    setTimeLeft(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const formatTime = (secs) =>
    `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  React.useEffect(() => () => clearInterval(timerRef.current), []);

  const handleSendOtp = async () => {
    setMessage('');
    setError('');
    const formattedMobile = formatMobile();
    if (!formattedMobile) {
      setError('Enter valid 10 digit mobile number');
      return;
    }
    if (isSignup && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name');
      return;
    }
    try {
      setLoading(true);
      const res = await sendOtp(formattedMobile);
      if (res.response_code === 200) {
        setMessage('OTP sent successfully');
        setMobile(formattedMobile);
        setStep(2);
        startTimer(60);
      } else {
        setError(
          res.errors?.[0] || res.response_message || 'Error sending OTP'
        );
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setMessage('');
    if (!otp.trim()) {
      setError('Enter OTP');
      return;
    }
    try {
      setLoading(true);
      const res = await verifyOtp(mobile, otp.trim());
      if (res.response_code === 200) {
        const token = res.user?.token;
        const user = res.user;
        if (!token) {
          setError('Authentication token missing');
          return;
        }
        const hasName = user.first_name && user.last_name;

        if (isSignup && hasName) {
          login(user, token);
          if (!preventRedirect) navigate(-1);
          return;
        }

        if (!hasName) {
          if (firstName.trim() && lastName.trim()) {
            const nameRes = await saveName(
              token,
              firstName.trim(),
              lastName.trim(),
              `${mobile}@cardacademy.co`
            );
            if (nameRes.response_code !== 200) {
              setError(nameRes.errors?.[0] || 'Failed to save name');
              return;
            }
            const updatedUser = {
              ...user,
              first_name: firstName.trim(),
              last_name: lastName.trim(),
            };
            login(updatedUser, token);
            if (!preventRedirect) navigate(-1);
          } else {
            setVerifiedToken(token);
            setVerifiedUser(user);
            setIsNewUser(true);
            setStep(3);
          }
          return;
        }

        login(user, token);
        if (!preventRedirect) navigate(-1);
      } else {
        setError(res.errors?.[0] || res.response_message || 'Incorrect OTP');
      }
    } catch {
      setError('Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitName = async () => {
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your First and Last name to continue');
      return;
    }
    try {
      setLoading(true);
      const nameRes = await saveName(
        verifiedToken,
        firstName.trim(),
        lastName.trim(),
        `${mobile}@cardacademy.co`
      );
      if (nameRes.response_code !== 200) {
        setError(nameRes.errors?.[0] || 'Failed to save name');
        return;
      }
      const updatedUser = {
        ...verifiedUser,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };
      login(updatedUser, verifiedToken);
      if (!preventRedirect) navigate(-1);
    } catch {
      setError('Failed to save name. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (timeLeft > 0) return;
    setOtp('');
    setError('');
    handleSendOtp();
  };

  const handleChangePhone = () => {
    setStep(1);
    setOtp('');
    setError('');
    setMessage('');
    clearInterval(timerRef.current);
    setTimeLeft(0);
  };

  const switchToSignup = () => {
    setIsSignup(true);
    setStep(1);
    setError('');
    setMessage('');
    setMobile('');
    setOtp('');
    setFirstName('');
    setLastName('');
  };

  const switchToLogin = () => {
    setIsSignup(false);
    setStep(1);
    setError('');
    setMessage('');
    setMobile('');
    setOtp('');
    setFirstName('');
    setLastName('');
  };

  const displayPhone =
    mobile.startsWith('91') && mobile.length === 12 ? mobile.slice(2) : mobile;
  const maskedPhone =
    displayPhone.length >= 10
      ? `${displayPhone.slice(0, 2)}XXXXXX${displayPhone.slice(-2)}`
      : displayPhone;

  return (
    <div className={`auth-page${modal ? ' auth-page--modal' : ''}`}>
      {/* ✅ LeftPanel: auth-left is NOW inside auth-page__left-panel */}
      <LeftPanel />

      {/* ✅ Right panel: auth-right is NOW inside auth-page__right-panel */}
      <div className="auth-page__right-panel">
        <div className="auth-right">
          <h1 className="auth-right__title">
            {step === 3 ? 'Sign Up' : isSignup ? 'Sign Up' : 'Login'}
          </h1>

          <div className="auth-right__form-wrapper">
            {/* STEP 1: Phone (+ name if signup) */}
            {step === 1 && (
              <>
                <div className="auth-form">
                  {isSignup && (
                    <div className="auth-field">
                      <label className="auth-field__label">Full Name</label>
                      <input
                        className="auth-field__input"
                        type="text"
                        placeholder="Enter First Name"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          setError('');
                        }}
                      />
                      <input
                        className="auth-field__input"
                        type="text"
                        placeholder="Enter Last Name"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          setError('');
                        }}
                        style={{ marginTop: 12 }}
                      />
                    </div>
                  )}
                  <div className="auth-field">
                    <label className="auth-field__label">Contact Number</label>
                    <input
                      className={`auth-field__input${error ? ' auth-field__input--error' : ''}`}
                      type="tel"
                      placeholder="Enter Mobile Number"
                      value={displayPhone}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.startsWith('91') && val.length === 12)
                          val = val.slice(2);
                        if (val.length > 10) val = val.slice(0, 10);
                        setMobile(val);
                        setError('');
                      }}
                    />
                  </div>
                  {error && <p className="auth-field__error-msg">{error}</p>}
                  {message && (
                    <p style={{ color: '#34C759', fontSize: 14, margin: 0 }}>
                      {message}
                    </p>
                  )}
                  <button
                    className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
                    onClick={handleSendOtp}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <SpinnerIcon /> Sending...
                      </>
                    ) : (
                      'REQUEST OTP'
                    )}
                  </button>
                </div>
                <div
                  className="auth-footer-link"
                  onClick={isSignup ? switchToLogin : switchToSignup}
                >
                  <span>
                    {isSignup
                      ? 'Existing User? Login'
                      : 'New to CardAcademy? Create an account'}
                  </span>
                </div>
              </>
            )}

            {/* STEP 2: OTP */}
            {step === 2 && (
              <>
                <div className="auth-form">
                  <div className="auth-field">
                    <div className="auth-field__label-row">
                      <span className="auth-field__label" style={{ flex: 1 }}>
                        Please enter the OTP sent to {maskedPhone}
                      </span>
                      <button
                        type="button"
                        className="auth-field__label-link"
                        onClick={handleChangePhone}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        Change
                      </button>
                    </div>
                    <input
                      className={`auth-field__input${error ? ' auth-field__input--error' : ''}`}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter OTP here"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                        setError('');
                      }}
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </div>
                  {error && <p className="auth-field__error-msg">{error}</p>}
                  {message && (
                    <p style={{ color: '#34C759', fontSize: 14, margin: 0 }}>
                      {message}
                    </p>
                  )}
                  <button
                    className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
                    onClick={handleVerifyOtp}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <SpinnerIcon /> Verifying...
                      </>
                    ) : (
                      'VERIFY OTP'
                    )}
                  </button>
                </div>
                <div className="auth-resend">
                  {timeLeft > 0 && (
                    <span className="auth-resend__timer">
                      {formatTime(timeLeft)}
                    </span>
                  )}
                  <span className="auth-resend__text">
                    Not received your code?{' '}
                    <span
                      className="auth-resend__text--link"
                      onClick={handleResend}
                      style={
                        timeLeft > 0
                          ? {
                              color: '#9999A1',
                              cursor: 'default',
                              fontWeight: 400,
                            }
                          : {}
                      }
                    >
                      Resend code
                    </span>
                  </span>
                </div>
              </>
            )}

            {/* STEP 3: New user name */}
            {step === 3 && (
              <div className="auth-form">
                <div className="auth-field">
                  <label className="auth-field__label">Full Name</label>
                  <input
                    className={`auth-field__input${error ? ' auth-field__input--error' : ''}`}
                    type="text"
                    placeholder="Enter First Name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setError('');
                    }}
                  />
                  <input
                    className={`auth-field__input${error ? ' auth-field__input--error' : ''}`}
                    type="text"
                    placeholder="Enter Last Name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setError('');
                    }}
                    style={{ marginTop: 12 }}
                  />
                </div>
                {error && <p className="auth-field__error-msg">{error}</p>}
                <button
                  className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
                  onClick={handleSubmitName}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <SpinnerIcon /> Submitting...
                    </>
                  ) : (
                    'SUBMIT'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
